/**
 * FlagDetailView — standalone detail page for a single FlagCatalogRow.
 * Not its own route: rendered by App.tsx in place of FeatureCatalogView's
 * list when the existing `flag-catalog` route carries a `flagId`.
 *
 * @see docs/superpowers/specs/2026-08-10-atlas-feature-flag-relationships-design.md Part 4
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { EmptyState } from '../components/EmptyState.js';
import { flagCatalogRowById } from '../state/selectors.js';
import { navigateToObject, navigateToObjectKind } from '../state/hashRouter.js';
import { KIND_META } from '../state/objectKinds.js';
import { useAppStore } from '../state/store.js';
import { WorkflowTrail } from './WorkflowTrail.js';

export interface FlagDetailViewProps {
  manifest: AtlasManifest;
  flagId: string;
}

export function FlagDetailView({ manifest, flagId }: FlagDetailViewProps): React.JSX.Element {
  const row = flagCatalogRowById(manifest, flagId);
  if (!row) {
    return <EmptyState icon="help" title={`Flag ${flagId} not found.`} />;
  }
  const { entityTrail } = useAppStore();

  return (
    <div className="detail-view">
      <ObjectHeader
        object={row}
        icon={KIND_META.flag.icon}
        iconLabel="Flag"
        iconColor={KIND_META.flag.color}
        badges={[]}
        title={row.flagId}
        docRef={<>{row.docSection} · {row.docRef}</>}
        description={row.description}
      />
      {entityTrail.length >= 2 && (
        <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
      )}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: 'var(--sp-md)' }}>
        <span className={`layer-badge lb-${row.category}`}>{row.category}</span>
        {row.tier && <span className="pack-gate-badge">{row.tier}</span>}
        {row.pack && <span className="pack-gate-badge">{row.pack}</span>}
      </div>
      <RelationshipsPanel manifest={manifest} kind="flag" id={row.flagId} onNavigate={navigateToObjectKind} />
    </div>
  );
}
